import React, { useEffect, useRef, useState } from 'react';
import { Eye, Target, Sparkles, ChevronRight, Award, Leaf, Shield, Users } from 'lucide-react';

const marqueeImages = [
  { src: "https://customer-assets.emergentagent.com/job_55c7756f-b73e-4654-b33e-a28bd5ef7fde/artifacts/b7kftclw_image.png", label: "Khalifa University - Main Campus" },
  { src: "https://customer-assets.emergentagent.com/job_55c7756f-b73e-4654-b33e-a28bd5ef7fde/artifacts/nnh05h2k_WhatsApp%20Image%202025-05-14%20at%204.34.52%20PM%20%286%29.jpg", label: "Mosque Landscaping" },
  { src: "https://customer-assets.emergentagent.com/job_portfolio-pro-505/artifacts/mmutp0kp_WhatsApp%20Image%202025-11-18%20at%2012.00.43_ca39209d.jpg", label: "ALDAR Saadiyat" },
  { src: "https://customer-assets.emergentagent.com/job_55c7756f-b73e-4654-b33e-a28bd5ef7fde/artifacts/jrt6g0ju_WhatsApp%20Image%202025-05-14%20at%204.34.51%20PM%20%283%29.jpeg", label: "Sunset Pathways" },
  { src: "https://customer-assets.emergentagent.com/job_55c7756f-b73e-4654-b33e-a28bd5ef7fde/artifacts/ucwst6th_68c255a7b3ef745dd07567df.jpg", label: "Al Khaznah Park" },
  { src: "https://customer-assets.emergentagent.com/job_55c7756f-b73e-4654-b33e-a28bd5ef7fde/artifacts/wv8n96mz_Screenshot%202025-11-24%20101558.png", label: "Urban Planting" },
  { src: "https://customer-assets.emergentagent.com/job_55c7756f-b73e-4654-b33e-a28bd5ef7fde/artifacts/5m50nius_6832c8b7d80194dd93595b1d.jpg", label: "Grand Mosque" },
  { src: "https://customer-assets.emergentagent.com/job_55c7756f-b73e-4654-b33e-a28bd5ef7fde/artifacts/0h056a2y_Screenshot%202025-11-24%20101536.png", label: "Hardscaping" },
  { src: "https://customer-assets.emergentagent.com/job_portfolio-pro-505/artifacts/3yunafgb_Joko-Widodo.jpeg", label: "Joko Widodo Mosque" },
  { src: "https://customer-assets.emergentagent.com/job_55c7756f-b73e-4654-b33e-a28bd5ef7fde/artifacts/4ns8q4b5_702%2010.jpg", label: "Palm Hardscaping" },
];

const aboutData = {
  overview: {
    title: "Who We Are",
    text: "At GREEN ART LANDSCAPE AND GARDENING SERVICES, we have been dedicated to transforming outdoor spaces into captivating and functional environments for years. Our commitment to excellence, attention to details, and firm passion for creating beautiful, sustainable outdoor spaces have earned us a strong reputation in the industry and made our vast number of clients happy and satisfied.",
    tagline: "We transform Visions & Designs to Reality"
  },
  vision: {
    icon: Eye,
    title: "Our Vision",
    subtitle: "Your Vision, Our Expertise — Growing Together",
    text: "To become Abu Dhabi's trusted leader in landscape design, known for creating lush, enduring green spaces that honor the UAE's unique environment. We envision a future where every landscape we build contributes to a more sustainable, greener, and more beautiful Abu Dhabi, inspiring communities and fostering respect for nature in the region."
  },
  mission: {
    icon: Target,
    title: "Our Mission",
    subtitle: "Towards a Sustainable Agricultural Future",
    text: "To craft sustainable and vibrant landscapes that celebrate UAE and Abu Dhabi's natural beauty and reflect the character of the UAE. Our focus is on creating green spaces that thrive in the desert climate, using native plants and innovative water-saving techniques. Through every project, we aim to enhance community well-being, support biodiversity, and set new standards in environmentally responsible landscaping."
  },
  values: [
    { icon: Sparkles, title: "Excellence", desc: "Where commitment meets excellence in every project we deliver" },
    { icon: Users, title: "Client Satisfaction", desc: "Focused on your landscape, committed to your satisfaction" },
    { icon: Shield, title: "Dedication", desc: "Working day & night, driven by dedication and powered by precision" },
    { icon: Leaf, title: "Sustainability", desc: "Your green goals are our priority — diverse products and sustainable solutions" }
  ]
};

const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [threshold]);

  return [ref, isVisible];
};

const AboutUs = () => {
  const [overviewRef, overviewVisible] = useInView();
  const [cardsRef, cardsVisible] = useInView();
  const [valuesRef, valuesVisible] = useInView();
  const [activeTab, setActiveTab] = useState('vision');

  const activeContent = activeTab === 'vision' ? aboutData.vision : aboutData.mission;
  const ActiveIcon = activeContent.icon;

  return (
    <section id="about-section" className="about-section" aria-label="About Us">
      <div className="container">

        {/* Section Header */}
        <div className="section-header">
          <span className="section-badge">About Us</span>
          <h2 className="section-title">Our Story</h2>
        </div>

        {/* Overview Card */}
        <div
          ref={overviewRef}
          className={`about-overview ${overviewVisible ? 'about-animate-in' : 'about-hidden'}`}
          data-testid="about-overview"
        >
          <div className="about-overview-accent"></div>
          <div className="about-overview-content">
            <h3 className="about-overview-tagline">{aboutData.overview.tagline}</h3>
            <p className="about-overview-text">{aboutData.overview.text}</p>
          </div>
        </div>

        {/* Project Showcase Marquee */}
        <div className="about-marquee-wrapper" data-testid="about-marquee">
          <div className="about-marquee-track">
            {[...marqueeImages, ...marqueeImages].map((img, i) => (
              <div key={i} className="about-marquee-slide">
                <img src={img.src} alt={img.label} className="about-marquee-img" loading="lazy" />
                <div className="about-marquee-label">{img.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Vision & Mission Interactive Tabs */}
        <div
          ref={cardsRef}
          className={`about-vm-container ${cardsVisible ? 'about-animate-in' : 'about-hidden'}`}
          data-testid="about-vision-mission"
        >
          {/* Tab Switcher */}
          <div className="about-tab-switcher">
            <button
              className={`about-tab ${activeTab === 'vision' ? 'about-tab-active' : ''}`}
              onClick={() => setActiveTab('vision')}
              data-testid="about-vision-tab"
            >
              <Eye className="w-5 h-5" />
              Vision
            </button>
            <button
              className={`about-tab ${activeTab === 'mission' ? 'about-tab-active' : ''}`}
              onClick={() => setActiveTab('mission')}
              data-testid="about-mission-tab"
            >
              <Target className="w-5 h-5" />
              Mission
            </button>
          </div>

          {/* Active Tab Content */}
          <div className="about-vm-card" key={activeTab}>
            <div className="about-vm-icon-wrap">
              <ActiveIcon className="w-10 h-10" />
            </div>
            <h3 className="about-vm-title">{activeContent.title}</h3>
            <p className="about-vm-subtitle">{activeContent.subtitle}</p>
            <div className="about-vm-divider"></div>
            <p className="about-vm-text">{activeContent.text}</p>
          </div>
        </div>

        {/* Core Values Grid */}
        <div
          ref={valuesRef}
          className={`about-values-section ${valuesVisible ? 'about-animate-in' : 'about-hidden'}`}
          data-testid="about-values"
        >
          <h3 className="about-values-heading">Our Core Values</h3>
          <div className="about-values-grid">
            {aboutData.values.map((value, index) => {
              const ValueIcon = value.icon;
              return (
                <div
                  key={index}
                  className="about-value-card"
                  style={{ animationDelay: `${index * 0.12}s` }}
                >
                  <div className="about-value-icon">
                    <ValueIcon className="w-6 h-6" />
                  </div>
                  <h4 className="about-value-title">{value.title}</h4>
                  <p className="about-value-desc">{value.desc}</p>
                  <ChevronRight className="about-value-arrow w-4 h-4" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats Bar */}
        <div className="about-stats-bar">
          <div className="about-stat-item">
            <Award className="w-6 h-6" />
            <span className="about-stat-num">4</span>
            <span className="about-stat-label">ISO Certifications</span>
          </div>
          <div className="about-stat-divider"></div>
          <div className="about-stat-item">
            <Users className="w-6 h-6" />
            <span className="about-stat-num">15+</span>
            <span className="about-stat-label">Major Projects</span>
          </div>
          <div className="about-stat-divider"></div>
          <div className="about-stat-item">
            <Shield className="w-6 h-6" />
            <span className="about-stat-num">24+</span>
            <span className="about-stat-label">Trusted Partners</span>
          </div>
          <div className="about-stat-divider"></div>
          <div className="about-stat-item">
            <Leaf className="w-6 h-6" />
            <span className="about-stat-num">8</span>
            <span className="about-stat-label">Service Lines</span>
          </div>
        </div>

      </div>
    </section>
  );
};

export default AboutUs;
